import { makeAutoObservable } from 'mobx';
import { ConstantField, MappedHeader } from '@shared/types/flowstep/mapping';
import WithValidation, { ValidationErrorsMap } from 'src/v2/states/validation';
import { ColumnFieldMappingEditorState } from './ColumnFieldMapping/ColumnFieldMappingEditor';
import { DataField } from 'shared/providers/types';

type MappingManagerErrorKey = 'mapping-error' | string;

export class MappingManagerState implements WithValidation<MappingManagerErrorKey> {
  editorStates: ColumnFieldMappingEditorState[] = [];
  constantFields: ConstantField[] = [];
  filterString: string = '';
  idField?: DataField;

  constructor() {
    makeAutoObservable(this);
  }

  get usedEditors() {
    return this.editorStates.filter((editorState) => editorState.checked);
  }

  initialize(
    mappedHeaders: MappedHeader[],
    objectFields: DataField[],
    constants: ConstantField[],
    idFieldName?: string,
  ) {
    this.editorStates = mappedHeaders.map((mappedHeader) => ({
      columnName: mappedHeader.excelColumnName,
      fieldName: mappedHeader.providerField?.name || '',
      checked: mappedHeader.isUsed,
    }));

    this.constantFields = constants;

    if (idFieldName) {
      this.idField = objectFields.find((field) => field.name === idFieldName);
    }
  }

  buildMappedHeaders(objectFields: DataField[]) {
    return this.editorStates.map<MappedHeader>((editorState) => {
      const objectField = objectFields.find((field) => field.name === editorState.fieldName);
      return {
        excelColumnName: editorState.columnName,
        isUsed: editorState.checked,
        providerField: objectField,
      };
    });
  }

  setAllCheckbox(checked: boolean) {
    this.editorStates.forEach((editorState) => (editorState.checked = checked));
  }

  get hasAllColumnsChecked() {
    return this.editorStates.every((editorState) => editorState.checked);
  }

  get filteredEditorStates() {
    return this.filterString
      ? this.editorStates.filter((editorState) =>
          editorState.columnName.toUpperCase().includes(this.filterString.toUpperCase()),
        )
      : this.editorStates;
  }

  private validateEditors(errors: Map<string, string>) {
    for (const editorState of this.usedEditors) {
      if (editorState.fieldName) {
        const anotherEditorState = this.usedEditors.find(
          (anotherState) => anotherState.fieldName === editorState.fieldName,
        );
        if (anotherEditorState !== editorState) {
          errors.set(
            editorState.columnName,
            `Field '${editorState.fieldName}' is already mapped to column '${anotherEditorState?.columnName}'`,
          );
          continue;
        }

        const constant = this.constantFields.find((constant) => constant.field.name === editorState.fieldName);
        if (constant) {
          errors.set(editorState.columnName, `Field '${editorState.fieldName}' is already used as a constant`);
          continue;
        }
      }

      if (!editorState.fieldName && editorState.checked) {
        errors.set(editorState.columnName, `Selected field cannot be empty`);
      }
    }
  }

  private validateIdField(errors: Map<string, string>) {
    if (this.idField) {
      const idFieldEditor = this.usedEditors.find((editorState) => editorState.fieldName === this.idField?.name);
      if (!idFieldEditor || !idFieldEditor.checked) {
        errors.set('id-field-error', `Field '${this.idField.label}' must participate in mapping`);
      }
    }
  }

  get validationErrors() {
    const errors = new Map<string, string>();
    this.validate(errors);
    return errors;
  }

  validate(errors: ValidationErrorsMap<string>): void {
    this.validateIdField(errors);
    this.validateEditors(errors);
  }
}
